/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  Colors,
  Dropdown,
  Field as ZinniaField,
  LabeledBooleanInput,
  TextInput,
} from '@zapier/design-system';
import { FormField } from '../../rest';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { COLOR_KEYS } from '../../constants';
import { useState } from 'react';

interface FieldProps extends FormField {
  value?: any;
  error?: string;
  isDisabled?: boolean;
  onChange: (value: any, isPristine: boolean) => void;
}

const Styles = labelStyles('FormField', {
  fieldset: css({
    border: 'none',
  }),

  helpText: css({
    fontSize: 12,
    color: Colors.gray60,
  }),

  checkboxLabel: css({
    display: 'flex',
    alignItems: 'center',
  }),

  checkboxLabelRequiredText: css({
    fontSize: '14px',
    color: Colors.neutral700,
    fontWeight: 400,
    marginLeft: '5px',
  }),

  checkboxError: css({
    fontSize: '14px',
    color: Colors.error400,
  }),
});

export function Field(props: FieldProps) {
  const [isPristine, setIsPristine] = useState(true);

  const handleChange = (value: any) => {
    props.onChange(value, isPristine);
    setIsPristine(false);
  };

  const fieldProps = {
    label: props.label,
    isRequired: props.isRequired,
    isDisabled: props.isDisabled,
    isHiddenIfPrefilled: props.isHiddenIfPrefilled,
    error: props.error,
    ...(props.helpText && {
      renderHelpText: () => (
        <div css={Styles.helpText}>
          <RichTextHtml
            html={props.helpText || ''}
            backgroundColor={COLOR_KEYS.neutral100}
          />
        </div>
      ),
    }),
  };

  if (props.type === 'checkbox') {
    return (
      <div>
        <LabeledBooleanInput
          type="checkbox"
          name={props.name}
          checked={props.value}
          isDisabled={props.isDisabled}
          isErrored={!!props.error}
          onChange={(event) => {
            handleChange((event.currentTarget as HTMLInputElement).checked);
          }}
        >
          <span css={Styles.checkboxLabel}>
            <span>{props.label}</span>
            {props.isRequired && (
              <span css={Styles.checkboxLabelRequiredText}>(required)</span>
            )}
          </span>
        </LabeledBooleanInput>
        {props.error && <div css={Styles.checkboxError}>{props.error}</div>}
      </div>
    );
  }

  if (props.type === 'checkbox-group' || props.type === 'radio-group') {
    return (
      <ZinniaField
        {...fieldProps}
        renderInput={() => (
          <fieldset css={Styles.fieldset}>
            {props.options?.map((option) => (
              <LabeledBooleanInput
                type={props.type === 'checkbox-group' ? 'checkbox' : 'radio'}
                key={option.value}
                name={props.name}
                value={option.value}
                checked={props.value?.includes(option.value)}
                isDisabled={props.isDisabled}
                onChange={(event) => {
                  let value: string | string[] = option.value;

                  if (props.type === 'checkbox-group') {
                    const previousValue = props.value || [];
                    value = (event.currentTarget as HTMLInputElement).checked
                      ? [...previousValue, value]
                      : previousValue.filter((v: string) => v !== value);
                  }
                  handleChange(value);
                }}
              >
                {option.label}
              </LabeledBooleanInput>
            ))}
          </fieldset>
        )}
      />
    );
  }

  if (props.type === 'dropdown') {
    return (
      <ZinniaField
        {...fieldProps}
        renderInput={(inputProps) => (
          <Dropdown
            {...inputProps}
            getLabelForItem={(item) => item.label}
            placeholder={props.placeholder}
            items={props.options || []}
            selectedItem={props.options?.find(
              (option) => option.value === props.value,
            )}
            onChange={(event) => handleChange(event.value)}
          />
        )}
      />
    );
  }

  return (
    <ZinniaField
      {...fieldProps}
      renderInput={(inputProps) => (
        <TextInput
          {...inputProps}
          name={props.name}
          value={props.value ?? ''}
          placeholder={props.placeholder}
          isFullWidth={true}
          onChange={(event) => {
            handleChange((event.target as HTMLInputElement).value);
          }}
          {...(props.type === 'textarea' && {
            isMultiline: true,
            rows: 4,
          })}
        />
      )}
    />
  );
}
